import { computed, Injectable, signal } from '@angular/core';
import { resolve, type Me as GqlMe } from '../api/gqty';
import { environment } from '../../environments/environment';

export type Me = Pick<
  GqlMe,
  '__typename' | 'id' | 'email' | 'firstName' | 'lastName' | 'roles' | 'isEnabled'
>;

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Currently authenticated user. `null` until `loadMe()` resolves. */
  readonly currentUser = signal<Me | null>(null);

  readonly isAdmin = computed(() => this.hasRole('admin'));
  readonly isManager = computed(() => this.hasRole('manager') || this.hasRole('admin'));

  hasRole(role: string): boolean {
    return this.currentUser()?.roles.includes(role) ?? false;
  }

  /**
   * Load the current user from the backend via GraphQL.
   * Called once by `authGuard` on app init.
   * Throws with `{ status: 401 }` if unauthenticated.
   */
  async loadMe(): Promise<Me> {
    let result: Me | null;

    try {
      result = await resolve(({ query }) => {
        const me = query.me;
        if (!me) return null;
        return {
          __typename: me.__typename,
          id: me.id,
          email: me.email,
          firstName: me.firstName,
          lastName: me.lastName,
          roles: me.roles,
          isEnabled: me.isEnabled,
        } satisfies Me;
      });
    } catch (error: unknown) {
      // Only map actual authentication errors to 401.
      // Network/server errors should propagate unchanged to avoid redirect loops.
      if (this.isAuthError(error)) {
        throw Object.assign(new Error('Unauthorised'), { status: 401 });
      }
      // Rethrow all other errors (network failures, server errors, etc.)
      throw error;
    }

    if (!result) {
      throw Object.assign(new Error('Unauthorised'), { status: 401 });
    }

    this.currentUser.set(result);
    return result;
  }

  /**
   * Log the current user out via the REST logout endpoint.
   * This is a full-page redirect (not a fetch): the backend clears the auth
   * cookie, logs out of Keycloak, and redirects back to `redirectUri`.
   */
  logout(redirectUri = '/'): void {
    this.currentUser.set(null);
    const url = `${environment.apiBasePath}/auth/logout?redirectUri=${encodeURIComponent(redirectUri)}`;
    window.location.href = url;
  }

  /**
   * Check if an error is an authentication/authorization error.
   * GraphQL errors may include extensions with error codes or specific error types.
   */
  private isAuthError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    // Check for GraphQL error with extensions.code
    if ('graphQLErrors' in error && Array.isArray((error as { graphQLErrors: unknown[] }).graphQLErrors)) {
      const graphQLErrors = (error as { graphQLErrors: { extensions?: { code?: string } }[] }).graphQLErrors;
      return graphQLErrors.some(
        (err) =>
          err.extensions?.code === 'AUTH_NOT_AUTHENTICATED' ||
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.extensions?.code === 'UNAUTHORIZED'
      );
    }

    // Check for direct error with extensions
    if ('extensions' in error) {
      const { code } = (error as { extensions?: { code?: string } }).extensions || {};
      if (
        code === 'AUTH_NOT_AUTHENTICATED' ||
        code === 'UNAUTHENTICATED' ||
        code === 'UNAUTHORIZED'
      ) {
        return true;
      }
    }

    // Check error message patterns (less reliable but catches some cases)
    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
      const message = (error as { message: string }).message.toLowerCase();
      return (
        message.includes('unauthenticated') ||
        message.includes('not authenticated') ||
        message.includes('unauthorized') ||
        message.includes('not authorized')
      );
    }

    return false;
  }
}
